import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useTRPC } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { FileIcon, ImageIcon, VideoIcon } from "lucide-react";

export const Route = createFileRoute("/_authenticated/media/grid")({
  component: () => <MediaGrid />,
});

function MediaGrid() {
  const trpc = useTRPC();
  const { data, isLoading, isError } = useQuery(trpc.media.list.queryOptions());

  if (isLoading) {
    const SkeletonPlaceholder = () => (
      <div className="h-48 w-full bg-muted animate-pulse rounded" />
    );

    return (
      <div className="container py-6">
        <h1 className="text-2xl font-bold mb-6">Media Library</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <SkeletonPlaceholder />
              <CardContent className="pt-4">
                <div className="h-4 w-2/3 mb-2 bg-muted animate-pulse rounded" />
                <div className="h-3 w-full bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container py-6">
        <div className="bg-destructive/15 text-destructive p-4 rounded-md">
          Error loading media. Please try again later.
        </div>
      </div>
    );
  }

  // If no data or empty array
  if (!data || data.length === 0) {
    return (
      <div className="container py-6">
        <h1 className="text-2xl font-bold mb-6">Media Library</h1>
        <div className="bg-muted p-8 text-center rounded-md">
          <p className="text-muted-foreground">No media items found</p>
        </div>
      </div>
    );
  }

  const getMediaTypeIcon = (mimeType: string | null) => {
    if (!mimeType) return <FileIcon className="h-5 w-5" />;

    if (mimeType.startsWith("image/")) {
      return <ImageIcon className="h-5 w-5" />;
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else if (mimeType.startsWith("video/")) {
      return <VideoIcon className="h-5 w-5" />;
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else {
      return <FileIcon className="h-5 w-5" />;
    }
  };

  const getMediaTypeLabel = (mimeType: string | null) => {
    if (!mimeType) return "File";

    if (mimeType.startsWith("image/")) {
      return "Image";
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else if (mimeType.startsWith("video/")) {
      return "Video";
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else {
      return "File";
    }
  };

  return (
    <div className="container py-6">
      <h1 className="text-2xl font-bold mb-6">Media Library</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {data.map((media) => (
          <Card
            key={media.id}
            className="overflow-hidden hover:shadow-md transition-all"
          >
            <div className="relative h-48 bg-muted">
              {media.cloudStorageObject.mimeType?.startsWith("image/") ? (
                <img
                  src={media.cloudStorageObject.fullUrl}
                  alt={media.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  {getMediaTypeIcon(media.cloudStorageObject.mimeType)}
                </div>
              )}
              <Badge
                className="absolute top-2 right-2 flex items-center gap-1"
                variant="secondary"
              >
                {getMediaTypeIcon(media.cloudStorageObject.mimeType)}
                <span>
                  {getMediaTypeLabel(media.cloudStorageObject.mimeType)}
                </span>
              </Badge>
            </div>
            <CardContent className="pt-4">
              <h3 className="font-medium text-sm line-clamp-1">
                {media.title}
              </h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {media.description}
              </p>
            </CardContent>
            <CardFooter className="text-xs text-muted-foreground pt-0 pb-3">
              {formatDistanceToNow(new Date(media.createdAt), {
                addSuffix: true,
              })}
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}
